import React, { useState, useEffect, ReactElement } from 'react';
import { ButtonGroup } from '@atlaskit/button';
import { Wrapper, Pulse, SpotlightHeading } from './styles';
import {
  Spotlight,
  SpotlightManager,
  SpotlightTarget,
  SpotlightTransition,
} from '@atlaskit/onboarding';

interface Props {
  children: ReactElement;
  userId: string;
  organizationId: string;
  featureName: string;
  spotlightHeading: string;
  spotlightBody: ReactElement;
}

/**
 *
 * This component is used to call out a new feature, making use of Atlaskit's onboarding components.
 * Once the "OK" button is clicked, it will perform an AJAX call to the back end and persist this action
 * so that the particular user won't see the callout again.
 *
 * If you need to make any customizations to this component to support your use case, please add it as a
 * passable prop so that current implementations' functionality is maintained.
 */
export default function FeatureCallout({
  children,
  userId,
  organizationId,
  featureName,
  spotlightHeading,
  spotlightBody,
}: Props) {
  const [showCallout, setShowCallout] = useState(true);
  const [, forceRender] = useState();

  // Workaround for Atlaskit bug when we have double border while resizing window. It is happening because
  // Atlaskit Onboarding component clones the target element. By triggering re-render we just rerender entire
  // spotlight, so under the hood Onboarding component destroys and re-creates all the necessary elements.
  useEffect(function() {
    window.addEventListener('resize', forceRender);

    return function() {
      window.removeEventListener('resize', forceRender);
    };
  }, []);

  function handleAck() {
    setShowCallout(false);

    $.ajax({
      type: 'POST',
      url: window.Routes.dismiss_feature_callout_organization_user_path(
        organizationId,
        userId,
      ),
      data: {
        feature_name: featureName,
      },
      dataType: 'json',
    });
  }

  if (!showCallout) {
    return children;
  }

  return (
    <Wrapper className="feature-callout">
      <SpotlightManager blanketIsTinted={false}>
        <ButtonGroup>
          <SpotlightTarget name={featureName}>
            <Pulse>{children}</Pulse>
          </SpotlightTarget>
        </ButtonGroup>
        <SpotlightTransition>
          <Spotlight
            target={featureName}
            heading={<SpotlightHeading>{spotlightHeading}</SpotlightHeading>}
            dialogPlacement="bottom right"
            dialogWidth="300"
            pulse={false}
            actions={[
              {
                onClick: handleAck,
                text: 'OK',
                autoFocus: true,
              },
            ]}
          >
            {spotlightBody}
          </Spotlight>
        </SpotlightTransition>
      </SpotlightManager>
    </Wrapper>
  );
}
